// frontend/src/pages/PurchaseOrderPage.js
import React, { useState, useEffect } from 'react';
import { 
    Search, ShoppingCart, ArrowLeft, Save, Loader2, 
    Building2, Package, Calendar as CalendarIcon, AlertCircle, Printer, User, ChevronLeft, ChevronRight,
    Plus, Edit, Trash2, FileText, Eye
} from 'lucide-react';
import useApi from '../hooks/useApi';

const PurchaseOrderPage = ({ currentUser, formatCurrency }) => {
    const { apiFetch } = useApi();
    
    // --- VERIFICAÇÃO DE PERMISSÕES GRANULARES ---
    const isSuper = currentUser?.is_super_user;
    const perms = currentUser?.permissions || {};
    const canCreate = isSuper || perms.purchase_order_create;
    const canEdit = isSuper || perms.purchase_order_edit;
    const canDelete = isSuper || perms.purchase_order_delete;

    // --- ESTADOS DE CONTROLE DE TELA ---
    const [viewMode, setViewMode] = useState('list'); 
    
    // --- ESTADOS DA LISTA DE PEDIDOS ---
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [poSearchQuery, setPoSearchQuery] = useState('');
    const [isFetchingPOs, setIsFetchingPOs] = useState(false);

    // --- ESTADOS DE BUSCA DE FORNECEDOR ---
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchingSupplier, setIsSearchingSupplier] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearchedSupplier, setHasSearchedSupplier] = useState(false);

    // --- ESTADOS DO FORMULÁRIO DO PEDIDO ---
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [paymentConditions, setPaymentConditions] = useState([]);
    const [supplierConditionId, setSupplierConditionId] = useState('');
    const [deliveryTimeText, setDeliveryTimeText] = useState('');
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
    const [orderStatus, setOrderStatus] = useState('Aguardando Chegada');
    const [observations, setObservations] = useState(''); 
    const [orderItems, setOrderItems] = useState([]);
    
    // --- ESTADOS DE IMPRESSÃO ---
    const [printData, setPrintData] = useState(null); 

    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Paginação
    const [page, setPage] = useState(1);
    const itemsPerPage = 12;

    const [entityData, setEntityData] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);

    // --- EFEITOS ---
    useEffect(() => {
        fetchPurchaseOrders();
        fetchConditions();
        fetchEntityData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (printData) {
            const timer = setTimeout(() => {
                window.print();
                setTimeout(() => setPrintData(null), 1000); 
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [printData]);

    const fetchConditions = async () => {
        try {
            const data = await apiFetch('/payment-conditions');
            setPaymentConditions(data || []);
        } catch (error) { console.error("Erro condições pagamento:", error); }
    };

    const fetchEntityData = async () => {
        if (currentUser && currentUser.entityId) {
            try {
                const entities = await apiFetch('/entities');
                const myEntity = entities.find(e => e.id === currentUser.entityId);
                setEntityData(myEntity || null);

                const token = localStorage.getItem('token');
                const logoRes = await fetch(`/api/entities/${currentUser.entityId}/logo?t=${new Date().getTime()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (logoRes.ok) {
                    const blob = await logoRes.blob();
                    setLogoUrl(URL.createObjectURL(blob));
                }
            } catch (error) { console.error("Erro dados entidade:", error); }
        }
    };

    // --- FUNÇÕES DA LISTA DE PEDIDOS ---
    const fetchPurchaseOrders = async (e) => {
        if (e) e.preventDefault();
        setIsFetchingPOs(true);
        try {
            const queryParam = poSearchQuery.trim() ? `?search=${encodeURIComponent(poSearchQuery)}` : '';
            const data = await apiFetch(`/purchase-orders${queryParam}`);
            setPurchaseOrders(data || []);
        } catch (error) {
            console.error("Erro ao buscar pedidos:", error);
            setPurchaseOrders([]);
        } finally {
            setIsFetchingPOs(false);
        }
    };

    const handleDeleteOrder = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir este pedido de compra? Esta ação não pode ser desfeita.")) return;
        try {
            await apiFetch(`/purchase-orders/${id}`, { method: 'DELETE' });
            alert("Pedido excluído com sucesso.");
            fetchPurchaseOrders();
        } catch (error) {
            alert(`Erro ao excluir: ${error.message}`);
        }
    };

    const handleEditOrderClick = async (order, readOnly = false) => {
        setIsLoadingData(true);
        setEditingOrderId(order.id);
        setIsReadOnly(readOnly); 
        
        try {
            const fullOrder = await apiFetch(`/purchase-orders/${order.id}`);
            
            setSelectedSupplier({
                id: fullOrder.supplier_id,
                name: fullOrder.supplier_name,
                document_id: fullOrder.document_id,
                billing_contact_name: fullOrder.billing_contact_name,
                email: fullOrder.email,
                phone: fullOrder.phone
            });

            setExpectedDeliveryDate(fullOrder.expected_delivery_date || '');
            setOrderStatus(fullOrder.status || 'Aguardando Chegada');
            setSupplierConditionId(fullOrder.payment_condition_id || ''); 
            setObservations(fullOrder.observations || ''); 
            setDeliveryTimeText('');

            const mappedItems = fullOrder.items.map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                packaging_type: item.packaging_type,
                negotiated_price: item.unit_price, 
                quantity: item.quantity,
                total: item.total_price
            }));
            
            setOrderItems(mappedItems);
            setViewMode('form');

        } catch (error) {
            console.error("Erro ao carregar pedido:", error);
            alert("Erro ao carregar os dados do pedido.");
            setViewMode('list');
        } finally {
            setIsLoadingData(false);
        }
    };

    const handlePrintOrderClick = async (order) => {
        setIsLoadingData(true);
        try {
            const fullOrder = await apiFetch(`/purchase-orders/${order.id}`);
            
            const mappedItems = fullOrder.items.map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                packaging_type: item.packaging_type,
                negotiated_price: item.unit_price,
                quantity: item.quantity,
                total: item.total_price
            }));

            setPrintData({
                orderId: fullOrder.id,
                supplier: {
                    name: fullOrder.supplier_name,
                    document_id: fullOrder.document_id,
                    billing_contact_name: fullOrder.billing_contact_name,
                    email: fullOrder.email,
                    phone: fullOrder.phone
                },
                expectedDeliveryDate: fullOrder.expected_delivery_date || '',
                supplierConditionId: fullOrder.payment_condition_id || '',
                observations: fullOrder.observations || '', 
                items: mappedItems,
                orderGrandTotal: fullOrder.total_amount || 0
            });
        } catch (error) {
            console.error("Erro ao carregar dados para impressão:", error);
            alert("Erro ao preparar o pedido para impressão.");
        } finally {
            setIsLoadingData(false);
        }
    };

    // --- FUNÇÕES DE BUSCA DE FORNECEDOR ---
    const handleSupplierSearchSubmit = async (e) => {
        if (e) e.preventDefault();
        setIsSearchingSupplier(true);
        setHasSearchedSupplier(true);
        setPage(1);

        try {
            const queryParam = searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const data = await apiFetch(`/general-clients?classification=Fornecedor${queryParam}`);
            setSearchResults(data || []);
        } catch (error) {
            console.error("Erro ao buscar fornecedores:", error);
            setSearchResults([]);
        } finally {
            setIsSearchingSupplier(false);
        }
    };

    const handleSelectSupplierForNewOrder = async (supplier) => {
        setSelectedSupplier(supplier);
        setSearchResults([]); 
        setSearchQuery(''); 
        setEditingOrderId(null); 
        setIsReadOnly(false);
        setIsLoadingData(true);
        setViewMode('form'); 

        try {
            const data = await apiFetch(`/supplier-negotiations/${supplier.id}/products`);
            
            const itemsForOrder = (data.items || [])
                .filter(item => item.negotiated_price > 0)
                .map(item => ({ ...item, quantity: '', total: 0 }));
            
            setOrderItems(itemsForOrder);
            setSupplierConditionId(data.payment_condition_id || '');
            setDeliveryTimeText(data.delivery_time || '');
            setObservations(''); 
            setOrderStatus('Aguardando Chegada');
            
            if (data.delivery_time) {
                const daysMatch = String(data.delivery_time).match(/(\d+)/);
                if (daysMatch && daysMatch[1]) {
                    const targetDate = new Date();
                    targetDate.setDate(targetDate.getDate() + parseInt(daysMatch[1], 10));
                    setExpectedDeliveryDate(targetDate.toISOString().split('T')[0]);
                } else { setExpectedDeliveryDate(''); }
            } else { setExpectedDeliveryDate(''); }

        } catch (error) {
            console.error("Erro ao buscar negociação:", error);
            setOrderItems([]);
        } finally {
            setIsLoadingData(false);
        }
    };

    // --- MANIPULAÇÃO DO FORMULÁRIO DO PEDIDO ---
    const handleQuantityChange = (index, value) => {
        if (isReadOnly) return;
        const qty = parseFloat(value) || 0;
        const newItems = [...orderItems];
        newItems[index].quantity = value; 
        newItems[index].total = qty * (newItems[index].negotiated_price || 0);
        setOrderItems(newItems);
    };

    const orderGrandTotal = orderItems.reduce((acc, item) => acc + (item.total || 0), 0);
    const hasItemsWithQuantity = orderItems.some(item => parseFloat(item.quantity) > 0);

    const handleSaveOrder = async () => {
        if (isReadOnly) return;
        if (!expectedDeliveryDate) return alert("Preencha a Data Prevista de Entrega.");
        const itemsToBuy = orderItems.filter(item => parseFloat(item.quantity) > 0);
        if (itemsToBuy.length === 0) return alert("Preencha a quantidade de pelo menos um produto.");

        setIsSaving(true);
        try {
            const payload = {
                supplier_id: selectedSupplier.id,
                expected_delivery_date: expectedDeliveryDate,
                status: orderStatus,
                total_amount: orderGrandTotal,
                payment_condition_id: supplierConditionId,
                observations: observations, 
                items: itemsToBuy.map(item => ({
                    product_id: item.product_id,
                    packaging_type: item.packaging_type,
                    unit_price: item.negotiated_price,
                    quantity: parseFloat(item.quantity),
                    total_price: item.total
                }))
            };
            
            if (editingOrderId) {
                await apiFetch(`/purchase-orders/${editingOrderId}`, { method: 'PUT', body: payload });
                alert('Pedido atualizado com sucesso!');
            } else {
                await apiFetch(`/purchase-orders`, { method: 'POST', body: payload });
                alert('Pedido de Compra gerado com sucesso!');
            }
            
            resetFormAndReturnToList();

        } catch (error) {
            console.error("Erro detalhado ao salvar:", error);
            alert(`Falha ao salvar: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    const resetFormAndReturnToList = () => {
        setSelectedSupplier(null);
        setExpectedDeliveryDate('');
        setOrderItems([]);
        setEditingOrderId(null);
        setObservations('');
        setIsReadOnly(false);
        setHasSearchedSupplier(false);
        setSearchResults([]);
        setViewMode('list');
        fetchPurchaseOrders();
    };

    const getConditionName = (id) => {
        const cond = paymentConditions.find(c => c.id == id);
        return cond ? cond.name : 'Não definida';
    };

    // --- RENDERIZADORES DE TELA ---
    
    // 1. TELA: LISTA DE PEDIDOS
    const renderListView = () => (
        <div className="animate-in fade-in duration-300 print:hidden">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                        <ShoppingCart className="text-blue-600" size={28} /> Pedidos de Compra
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Gerencie os pedidos efetuados para seus fornecedores.</p>
                </div>
                <div className="flex w-full lg:w-auto items-center gap-3">
                    <form onSubmit={fetchPurchaseOrders} className="flex gap-2 flex-1 lg:w-80">
                        <div className="relative flex-1">
                            <input 
                                type="text" 
                                placeholder="Buscar por ID ou Fornecedor..." 
                                value={poSearchQuery}
                                onChange={(e) => setPoSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm h-full outline-none"
                            />
                            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                        </div>
                        <button type="submit" className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 p-2.5 rounded-lg shadow-sm transition-colors shrink-0">
                            <Search size={20} />
                        </button>
                    </form>
                    
                    {/* SÓ MOSTRA O BOTÃO "NOVO PEDIDO" SE TIVER PERMISSÃO DE CREATE */}
                    {canCreate && (
                        <button 
                            onClick={() => setViewMode('supplier_select')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg shadow-sm transition-colors flex items-center justify-center shrink-0 font-semibold gap-2"
                        >
                            <Plus size={18} /> Novo Pedido
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {isFetchingPOs ? (
                    <div className="p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                ) : purchaseOrders.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                        <ShoppingCart size={48} className="text-gray-300 mb-3" />
                        <p className="font-medium text-gray-700 mb-1">Nenhum pedido encontrado.</p>
                        <p className="text-sm">Clique em "Novo Pedido" para criar sua primeira ordem de compra.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600 font-semibold uppercase text-[10px] border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3">Nº Pedido</th>
                                    <th className="px-6 py-3">Fornecedor</th>
                                    <th className="px-6 py-3">Data Prevista</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Valor Total</th>
                                    <th className="px-6 py-3 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {purchaseOrders.map((order) => (
                                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-bold text-gray-700">#{order.id.toString().padStart(4, '0')}</td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-800">{order.supplier_name}</div>
                                            <div className="text-[10px] text-gray-500">Doc: {order.document_id || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {order.expected_delivery_date ? order.expected_delivery_date.split('-').reverse().join('/') : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-[10px] font-bold px-2 py-1 bg-yellow-100 text-yellow-800 rounded uppercase">
                                                {order.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-green-600">
                                            {formatCurrency ? formatCurrency(order.total_amount) : `R$ ${order.total_amount}`}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button onClick={() => handleEditOrderClick(order, true)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Visualizar Pedido">
                                                    <Eye size={16} />
                                                </button>
                                                
                                                {/* SÓ MOSTRA O BOTÃO "EDITAR" SE TIVER PERMISSÃO DE EDIT */}
                                                {canEdit && (
                                                    <button onClick={() => handleEditOrderClick(order, false)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Editar Pedido">
                                                        <Edit size={16} />
                                                    </button>
                                                )}

                                                <button onClick={() => handlePrintOrderClick(order)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors" title="Imprimir PDF do Pedido">
                                                    <Printer size={16} />
                                                </button>

                                                {/* SÓ MOSTRA O BOTÃO "EXCLUIR" SE TIVER PERMISSÃO DE DELETE */}
                                                {canDelete && (
                                                    <button onClick={() => handleDeleteOrder(order.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors" title="Excluir Pedido">
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    // 2. TELA: SELECIONAR FORNECEDOR
    const renderSupplierSelectView = () => {
        const indexOfLastItem = page * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentSuppliers = searchResults.slice(indexOfFirstItem, indexOfLastItem);
        const totalPages = Math.ceil(searchResults.length / itemsPerPage);

        return (
            <div className="animate-in slide-in-from-right-4 duration-300 print:hidden">
                <div className="flex items-center gap-4 mb-6">
                    <button onClick={() => setViewMode('list')} className="text-gray-500 hover:text-gray-800 transition-colors p-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Selecione o Fornecedor</h2>
                        <p className="text-sm text-gray-500">Etapa 1 de 2: Para quem será este pedido?</p>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center mb-6">
                    <form onSubmit={handleSupplierSearchSubmit} className="flex gap-2 w-full max-w-2xl">
                        <div className="relative flex-1">
                            <input 
                                type="text" 
                                placeholder="Digite o nome, CNPJ ou CPF do fornecedor..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm outline-none text-base"
                            />
                            <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
                        </div>
                        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-sm transition-colors font-semibold">
                            {isSearchingSupplier ? <Loader2 size={20} className="animate-spin" /> : 'Buscar'}
                        </button>
                    </form>
                </div>

                {hasSearchedSupplier && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-in fade-in">
                        {searchResults.length === 0 ? (
                            <div className="p-12 text-center text-gray-500"><p>Nenhum fornecedor encontrado.</p></div>
                        ) : (
                            <div className="flex flex-col h-full">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs">
                                            <tr>
                                                <th className="px-6 py-3">Documento</th>
                                                <th className="px-6 py-3">Fornecedor</th>
                                                <th className="px-6 py-3 text-center">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {currentSuppliers.map((supplier) => (
                                                <tr key={supplier.id} className="hover:bg-blue-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">{supplier.document_type || 'DOC'}: {supplier.document_id || '-'}</td>
                                                    <td className="px-6 py-4 font-bold text-gray-800">{supplier.name}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button 
                                                            onClick={() => handleSelectSupplierForNewOrder(supplier)}
                                                            className="px-4 py-1.5 bg-white border border-blue-300 rounded hover:bg-blue-50 text-blue-600 font-semibold text-xs transition-colors"
                                                        >
                                                            Continuar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalPages > 1 && (
                                    <div className="p-4 border-t border-gray-200 bg-white flex flex-col sm:flex-row justify-between items-center gap-4">
                                        <span className="text-sm text-gray-500">Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, searchResults.length)} de {searchResults.length}</span>
                                        <div className="flex gap-2 items-center">
                                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded border border-gray-300 text-gray-600 disabled:opacity-50"><ChevronLeft size={18} /></button>
                                            <span className="text-sm font-medium px-3 py-1 bg-gray-100 rounded text-gray-700">Página {page} de {totalPages}</span>
                                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded border border-gray-300 text-gray-600 disabled:opacity-50"><ChevronRight size={18} /></button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // 3. TELA: FORMULÁRIO DO PEDIDO
    const renderFormView = () => (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300 print:hidden">
            <div className="flex justify-between items-center">
                <button onClick={resetFormAndReturnToList} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 font-medium transition-colors">
                    <ArrowLeft size={18} /> Voltar para Lista
                </button>
                <div className="flex gap-3">
                    {/* SÓ MOSTRA O BOTÃO DE SALVAR SE TIVER PERMISSÃO E NÃO FOR SOMENTE LEITURA */}
                    {!isReadOnly && (canCreate || canEdit) && (
                        <button onClick={handleSaveOrder} disabled={isSaving || !hasItemsWithQuantity} className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm font-semibold transition-colors disabled:bg-green-400">
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {isSaving ? 'Salvando...' : (editingOrderId ? 'Atualizar Pedido' : 'Salvar Pedido')}
                        </button>
                    )}
                </div>
            </div>

            {editingOrderId && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-blue-800 font-bold">
                        {isReadOnly ? <Eye size={20} /> : <FileText size={20} />}
                        {isReadOnly ? 'Visualizando' : 'Editando'} Pedido #{editingOrderId.toString().padStart(4, '0')}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-blue-800">Status do Pedido:</span>
                        <select 
                            value={orderStatus} 
                            onChange={(e) => setOrderStatus(e.target.value)}
                            disabled={isReadOnly}
                            className="text-sm border-gray-300 rounded focus:ring-blue-500 font-medium text-gray-700 disabled:bg-transparent disabled:border-transparent disabled:font-bold disabled:text-blue-800 disabled:opacity-100"
                        >
                            <option value="Aguardando Chegada">Aguardando Chegada</option>
                            <option value="Parcialmente Recebido">Parcialmente Recebido</option>
                            <option value="Concluído">Concluído</option>
                            <option value="Cancelado">Cancelado</option>
                        </select>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-inner">
                    <Building2 size={32} />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">{selectedSupplier?.name}</h2>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span><strong>CNPJ/CPF:</strong> {selectedSupplier?.document_id || 'Não informado'}</span>
                        <span><strong>Contato:</strong> {selectedSupplier?.billing_contact_name || '-'}</span>
                        <span><strong>E-mail:</strong> {selectedSupplier?.email || '-'}</span>
                    </div>
                </div>
            </div>

            <div className="bg-blue-50/30 rounded-xl shadow-sm border border-blue-100 p-6">
                <h3 className="font-bold text-blue-800 flex items-center gap-2 mb-4"><CalendarIcon size={18} /> Dados do Pedido</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Data Prevista de Entrega <span className="text-red-500">*</span></label>
                        {isReadOnly ? (
                            <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-gray-100 text-gray-700 font-medium">
                                {expectedDeliveryDate ? expectedDeliveryDate.split('-').reverse().join('/') : '-'}
                            </div>
                        ) : (
                            <input 
                                type="date" 
                                value={expectedDeliveryDate} 
                                onChange={(e) => setExpectedDeliveryDate(e.target.value)} 
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 bg-white shadow-sm outline-none"
                            />
                        )}
                        {deliveryTimeText && !editingOrderId && <p className="text-[10px] text-gray-500 mt-1 italic">Prazo acordado: {deliveryTimeText}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Condição de Pagamento</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-gray-100 text-gray-700 font-medium">{getConditionName(supplierConditionId)}</div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Comprador</label>
                        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-gray-100 text-gray-700 font-medium flex items-center gap-2"><User size={14}/> {currentUser?.username || 'Sistema'}</div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Observações do Pedido</label>
                    {isReadOnly ? (
                        <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded bg-gray-100 text-gray-700 min-h-[60px] whitespace-pre-wrap">
                            {observations || 'Nenhuma observação.'}
                        </div>
                    ) : (
                        <textarea 
                            value={observations}
                            onChange={(e) => setObservations(e.target.value)}
                            rows={3}
                            placeholder="Adicione instruções de entrega, condições especiais, etc."
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 bg-white shadow-sm outline-none resize-y"
                        />
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                    <Package size={18} className="text-blue-600" />
                    <h3 className="font-bold text-gray-800">Itens do Pedido</h3>
                </div>

                {isLoadingData ? (
                    <div className="p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                ) : orderItems.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                        <AlertCircle size={48} className="text-orange-400 mb-3" />
                        <p className="font-medium text-gray-700">Nenhum preço foi negociado com este fornecedor ainda.</p>
                        <p className="text-sm mt-1">Vá até "Negociação Fornecedor" e defina os valores antes de fazer o pedido.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm min-w-[700px]">
                            <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-[10px] border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3">PRODUTO / VARIAÇÃO</th>
                                    <th className="px-4 py-3 text-center">EMBALAGEM</th>
                                    <th className="px-4 py-3 text-right">VALOR UNIT. (R$)</th>
                                    <th className="px-4 py-3 w-32 text-center">QUANTIDADE</th>
                                    <th className="px-6 py-3 text-right">TOTAL ITEM (R$)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {orderItems.map((item, index) => {
                                    const hasQty = parseFloat(item.quantity) > 0;
                                    if (isReadOnly && !hasQty) return null;

                                    return (
                                        <tr key={index} className={`transition-colors ${hasQty ? 'bg-blue-50/30' : 'hover:bg-gray-50'}`}>
                                            <td className="px-6 py-3 font-medium text-gray-800">{item.product_name}</td>
                                            <td className="px-4 py-3 text-center"><span className="font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded text-xs">{item.packaging_type}</span></td>
                                            <td className="px-4 py-3 text-right font-medium text-gray-600">{formatCurrency ? formatCurrency(item.negotiated_price) : `R$ ${item.negotiated_price}`}</td>
                                            <td className="px-4 py-3 text-center">
                                                {isReadOnly ? (
                                                    <span className="font-bold text-blue-700 text-base">{item.quantity}</span>
                                                ) : (
                                                    <input 
                                                        type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                        className={`w-full px-2 py-1.5 border rounded outline-none text-center font-bold shadow-sm ${hasQty ? 'border-blue-400 bg-white text-blue-700' : 'border-gray-300 bg-gray-50 text-gray-700'}`} placeholder="0"
                                                    />
                                                )}
                                            </td>
                                            <td className={`px-6 py-3 text-right font-bold ${hasQty ? 'text-blue-700' : 'text-gray-400'}`}>
                                                {formatCurrency ? formatCurrency(item.total) : `R$ ${item.total}`}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                <tr>
                                    <td colSpan="4" className="px-6 py-4 text-right font-bold text-gray-700 uppercase text-xs">Valor Total do Pedido:</td>
                                    <td className="px-6 py-4 text-right font-bold text-xl text-green-600">{formatCurrency ? formatCurrency(orderGrandTotal) : `R$ ${orderGrandTotal}`}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const pSupplier = printData?.supplier || selectedSupplier;
    const pExpectedDate = printData?.expectedDeliveryDate || expectedDeliveryDate;
    const pConditionId = printData?.supplierConditionId || supplierConditionId;
    const pItems = printData?.items || orderItems;
    const pTotal = printData?.orderGrandTotal || orderGrandTotal;
    const pOrderId = printData?.orderId || editingOrderId;
    const pBuyerName = printData?.buyerName || currentUser?.username;
    const pObservations = printData?.observations || observations;

    return (
        <div className="p-6 max-w-7xl mx-auto font-sans">
            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        #printable-purchase-order, #printable-purchase-order * { visibility: visible; }
                        #printable-purchase-order { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
                        @page { margin: 10mm; }
                    }
                `}
            </style>

            {viewMode === 'list' && renderListView()}
            {viewMode === 'supplier_select' && renderSupplierSelectView()}
            {viewMode === 'form' && renderFormView()}

            {(printData || selectedSupplier) && (
                <div id="printable-purchase-order" className="hidden print:block w-full text-black font-sans bg-white">
                    <div className="flex items-center border-b-2 border-gray-800 pb-4 mb-6">
                        {logoUrl ? <img src={logoUrl} alt="Logo Entidade" className="w-24 h-24 object-contain mr-4" /> : <div className="w-24 h-24 bg-gray-200 flex items-center justify-center font-bold text-xs mr-4">Sem Logo</div>}
                        <div>
                            <h1 className="text-2xl font-bold uppercase">{entityData?.name || currentUser?.entityName || 'Empresa'}</h1>
                            <p className="text-sm mt-1">{entityData?.address_street ? `${entityData.address_street}, ${entityData.address_number || 'S/N'}` : ''}</p>
                            <p className="text-sm">{entityData?.address_city ? `${entityData.address_city} - ${entityData.address_state}` : ''} {entityData?.document_id ? ` | CNPJ: ${entityData.document_id}` : ''}</p>
                        </div>
                        <div className="ml-auto text-right">
                            <h2 className="text-xl font-bold uppercase border border-black p-2 rounded">PEDIDO DE COMPRA {pOrderId ? `#${pOrderId.toString().padStart(4, '0')}` : ''}</h2>
                            <p className="text-sm mt-2 font-medium">Emissão: {new Date().toLocaleDateString('pt-BR')}</p>
                            <p className="text-sm font-medium">Comprador: {pBuyerName}</p>
                        </div>
                    </div>

                    <div className="flex justify-between gap-4 mb-6">
                        <div className="border border-gray-400 p-3 w-1/2 rounded">
                            <h3 className="font-bold text-sm bg-gray-200 px-2 py-1 mb-2 uppercase">Dados do Fornecedor</h3>
                            <p className="text-sm"><strong>Nome:</strong> {pSupplier?.name}</p>
                            <p className="text-sm"><strong>CNPJ/CPF:</strong> {pSupplier?.document_id || '-'}</p>
                            <p className="text-sm"><strong>Contato:</strong> {pSupplier?.billing_contact_name || '-'}</p>
                            <p className="text-sm"><strong>E-mail:</strong> {pSupplier?.email || '-'}</p>
                        </div>
                        <div className="border border-gray-400 p-3 w-1/2 rounded">
                            <h3 className="font-bold text-sm bg-gray-200 px-2 py-1 mb-2 uppercase">Condições do Pedido</h3>
                            <p className="text-sm"><strong>Pagamento:</strong> {getConditionName(pConditionId)}</p>
                            <p className="text-sm"><strong>Entrega Prevista:</strong> {pExpectedDate ? pExpectedDate.split('-').reverse().join('/') : 'Não informada'}</p>
                            <p className="text-sm"><strong>Endereço de Entrega:</strong> Padrão da Empresa.</p>
                        </div>
                    </div>

                    {pObservations && (
                        <div className="mb-6 p-3 border border-gray-400 rounded">
                            <h3 className="font-bold text-sm bg-gray-200 px-2 py-1 mb-2 uppercase">Observações</h3>
                            <p className="text-sm whitespace-pre-wrap">{pObservations}</p>
                        </div>
                    )}

                    <table className="w-full border-collapse border border-gray-400 text-sm mb-6">
                        <thead>
                            <tr className="bg-gray-200">
                                <th className="border border-gray-400 p-2 text-left">Produto</th>
                                <th className="border border-gray-400 p-2 text-center">Emb.</th>
                                <th className="border border-gray-400 p-2 text-right">Qtd.</th>
                                <th className="border border-gray-400 p-2 text-right">V. Unitário (R$)</th>
                                <th className="border border-gray-400 p-2 text-right">Total (R$)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pItems.filter(item => parseFloat(item.quantity) > 0).map((item, index) => (
                                <tr key={index}>
                                    <td className="border border-gray-400 p-2">{item.product_name}</td>
                                    <td className="border border-gray-400 p-2 text-center">{item.packaging_type}</td>
                                    <td className="border border-gray-400 p-2 text-right">{item.quantity}</td>
                                    <td className="border border-gray-400 p-2 text-right">{new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(item.negotiated_price)}</td>
                                    <td className="border border-gray-400 p-2 text-right font-bold">{new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(item.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="4" className="border border-gray-400 p-2 text-right font-bold uppercase">Total do Pedido:</td>
                                <td className="border border-gray-400 p-2 text-right font-bold text-lg">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pTotal)}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div className="mt-16 flex justify-around text-sm font-medium">
                        <div className="text-center w-1/3"><div className="border-t border-black pt-2 w-full">Assinatura do Comprador</div></div>
                        <div className="text-center w-1/3"><div className="border-t border-black pt-2 w-full">Assinatura do Fornecedor</div></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrderPage;